/**
 * TopBar — Top navigation bar with menu toggle, search, notifications, user avatar.
 * F9 update: integrates NotificationCenter dropdown with live unread count from polling.
 * Issue 60: integrates PWA Install button and PWA Diagnostics panel.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Menu, Search, ChevronDown, User, Settings, LogOut, ShieldCheck, Bug } from 'lucide-react';
import { useAuth } from '@/features/auth/auth.hook';
import { Avatar } from './Avatar';
import { NotificationCenter } from '@/features/notifications/NotificationCenter';
import { useNotificationsQuery } from '@/features/notifications/hooks/useNotifications';
import { InstallPwaButton } from '@/components/InstallPwaButton';
import { PwaDiagnosticsPanel } from '@/components/PwaDiagnosticsPanel';
import { TelemetryDebugInfo } from '@/features/telemetry/TelemetryDebugInfo';

export interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [diagOpen, setDiagOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Fetch notifications for unread count (polls every 60s)
  const notifQ = useNotificationsQuery({ limit: 50 });
  const unreadCount = notifQ.data?.data.filter((n) => !n.isRead).length ?? 0;

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/decks?search=${encodeURIComponent(searchValue.trim())}`);
    }
  }

  function handleLogout() {
    setMenuOpen(false);
    logout();
  }

  return (
    <>
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 h-16 flex items-center px-4 gap-3 safe-top">
        {/* Menu toggle (mobile) */}
        <button
          onClick={onMenuClick}
          className="lg:hidden text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
          aria-label="Open navigation menu"
        >
          <Menu size={22} />
        </button>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 max-w-md hidden sm:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search decks, cards…"
              className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white"
              aria-label="Search"
            />
          </div>
        </form>

        <div className="flex-1 sm:hidden" />

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative">
            <button
              ref={bellRef}
              onClick={() => setNotifOpen((v) => !v)}
              className="relative text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              aria-expanded={notifOpen}
            >
              {/* Bell icon inline to show badge */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
              </svg>
              {unreadCount > 0 && (
                <span
                  className={clsx(
                    'absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full bg-danger-600 text-white text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-white',
                  )}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <NotificationCenter
              open={notifOpen}
              onClose={() => setNotifOpen(false)}
              triggerRef={bellRef}
            />
          </div>

          {/* PWA Install Button */}
          <InstallPwaButton />

          {/* User menu */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="User menu"
              aria-expanded={menuOpen}
            >
              <Avatar user={user} size="sm" />
              <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {/* User info */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.displayName || user?.username || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>

                {/* Menu items */}
                <button
                  onClick={() => { setMenuOpen(false); setDiagOpen(true); }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <ShieldCheck className="w-4 h-4" /> PWA Diagnostics
                </button>
                <button
                  onClick={() => { setMenuOpen(false); setDebugOpen(true); }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Bug className="w-4 h-4" /> Info Debug
                </button>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="w-4 h-4" /> Settings
                </button>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <User className="w-4 h-4" /> Profile
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-600 hover:bg-danger-50"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* PWA Diagnostics Panel overlay */}
      {diagOpen && <PwaDiagnosticsPanel onClose={() => setDiagOpen(false)} />}

      {/* Telemetry Debug Info modal (controlled) */}
      <TelemetryDebugInfo externalOpen={debugOpen} onExternalClose={() => setDebugOpen(false)} />
    </>
  );
}

export default TopBar;